/**
 * AdminGlossaryTab Component - GOSSA v2.0
 * Unified glossary management with multi-context support
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  Plus, Edit, Trash2, Loader2, BookOpen, Download, Upload,
  Languages, FileText, Globe, ChevronDown, ChevronUp,
  AlertCircle, CheckCircle
} from 'lucide-react';
import GlossaryImportDialog from './glossary/GlossaryImportDialog';
import GlossaryFilters from './glossary/GlossaryFilters';
import GlossaryStatsSummary from './glossary/GlossaryStatsSummary';
import GlossaryPreviewDialog from './glossary/GlossaryPreviewDialog';
import GlossaryFormDialog from './glossary/GlossaryFormDialog';

const API = (window.location.origin || '') + '/api';

// Available exam contexts for definitions
const EXAM_CONTEXTS = ['PMP', 'CAPM', 'PSM', 'PSPO', 'SAFe', 'ITIL', 'PRINCE2', 'Scrum'];

const AdminGlossaryTab = ({ getAuthHeaders }) => {
  const [terms, setTerms] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedTag, setSelectedTag] = useState('');
  const [stats, setStats] = useState(null);
  
  // Dialog states
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [editingTerm, setEditingTerm] = useState(null);
  const [previewTerm, setPreviewTerm] = useState(null);
  const [expandedRow, setExpandedRow] = useState(null);
  
  // Form state - GOSSA v2.0 schema
  const [formData, setFormData] = useState({
    term: '',
    master_definition: '',
    primary_source: '',
    context_definitions: {},
    references: [],
    category: 'General',
    tags: [],
    do_not_translate: true,
    version: '1.0'
  });
  
  // Temporary state for adding context definitions
  const [newContextExam, setNewContextExam] = useState('');
  const [newContextDef, setNewContextDef] = useState('');
  const [newContextRef, setNewContextRef] = useState('');
  
  // Temporary state for references and tags
  const [newReference, setNewReference] = useState('');
  const [newTag, setNewTag] = useState('');

  // Import settings
  const [importMergeStrategy, setImportMergeStrategy] = useState('skip');
  const [importJsonText, setImportJsonText] = useState('');
  const [importMethod, setImportMethod] = useState('file'); // 'file' or 'paste'
  const [importLoading, setImportLoading] = useState(false);
  const [importPreview, setImportPreview] = useState(null); // Preview data
  const [importPreviewError, setImportPreviewError] = useState('');

  // Fetch categories
  const fetchCategories = useCallback(async () => {
    try {
      const res = await fetch(`${API}/glossary/categories`, {
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setCategories(data.categories || []);
      }
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  }, [getAuthHeaders]);

  // Fetch terms
  const fetchTerms = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API}/glossary/terms?limit=200`;
      if (selectedCategory) url += `&category=${selectedCategory}`;
      if (selectedTag) url += `&tag=${encodeURIComponent(selectedTag)}`;
      if (searchTerm) url += `&search=${encodeURIComponent(searchTerm)}`;
      
      const res = await fetch(url, {
        headers: getAuthHeaders()
      });
      
      if (res.ok) {
        const data = await res.json();
        setTerms(data.terms || []);
      }
    } catch (error) {
      console.error('Failed to fetch terms:', error);
      toast.error('Fehler beim Laden der Begriffe');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, selectedCategory, selectedTag, searchTerm]);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const res = await fetch(`${API}/glossary/stats`, {
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchCategories();
    fetchStats();
  }, [fetchCategories, fetchStats]);

  useEffect(() => {
    fetchTerms();
  }, [fetchTerms]);

  // Reset form
  const resetForm = () => {
    setFormData({
      term: '',
      master_definition: '',
      primary_source: '',
      context_definitions: {},
      references: [],
      category: 'General',
      tags: [],
      do_not_translate: true,
      version: '1.0'
    });
    setNewContextExam('');
    setNewContextDef('');
    setNewContextRef('');
    setNewReference('');
    setNewTag('');
  };

  // Open edit dialog
  const openEditDialog = (term) => {
    setEditingTerm(term);
    setFormData({
      term: term.term,
      master_definition: term.master_definition || term.definition || '',
      primary_source: term.primary_source || '',
      context_definitions: term.context_definitions || {},
      references: term.references || [],
      category: term.category || 'General',
      tags: term.tags || [],
      do_not_translate: term.do_not_translate !== false,
      version: term.version || '1.0'
    });
    setShowEditDialog(true);
  };

  // Add context definition
  const addContextDefinition = () => {
    if (!newContextExam || !newContextDef) {
      toast.error('Exam und Definition sind erforderlich');
      return;
    }
    setFormData({
      ...formData,
      context_definitions: {
        ...formData.context_definitions,
        [newContextExam]: {
          definition: newContextDef,
          reference: newContextRef || null
        }
      }
    });
    setNewContextExam('');
    setNewContextDef('');
    setNewContextRef('');
  };

  // Remove context definition
  const removeContextDefinition = (exam) => {
    const newContexts = { ...formData.context_definitions };
    delete newContexts[exam];
    setFormData({ ...formData, context_definitions: newContexts });
  };

  // Add reference
  const addReference = () => {
    if (!newReference) return;
    if (formData.references.includes(newReference)) {
      toast.error('Referenz bereits vorhanden');
      return;
    }
    setFormData({
      ...formData,
      references: [...formData.references, newReference]
    });
    setNewReference('');
  };

  // Remove reference
  const removeReference = (ref) => {
    setFormData({
      ...formData,
      references: formData.references.filter(r => r !== ref)
    });
  };

  // Add tag
  const addTag = () => {
    if (!newTag) return;
    if (formData.tags.includes(newTag)) {
      toast.error('Tag bereits vorhanden');
      return;
    }
    setFormData({
      ...formData,
      tags: [...formData.tags, newTag]
    });
    setNewTag('');
  };

  // Remove tag
  const removeTag = (tag) => {
    setFormData({
      ...formData,
      tags: formData.tags.filter(t => t !== tag)
    });
  };

  // Create term
  const handleCreate = async () => {
    if (!formData.term || !formData.master_definition) {
      toast.error('Begriff und Master-Definition sind erforderlich');
      return;
    }

    try {
      const res = await fetch(`${API}/glossary/terms`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (res.ok) {
        toast.success('Begriff erfolgreich erstellt');
        setShowAddDialog(false);
        resetForm();
        fetchTerms();
        fetchStats();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Fehler beim Erstellen');
      }
    } catch (error) {
      toast.error('Fehler beim Erstellen des Begriffs');
    }
  };

  // Update term
  const handleUpdate = async () => {
    if (!formData.term || !formData.master_definition) {
      toast.error('Begriff und Master-Definition sind erforderlich');
      return;
    }

    try {
      const res = await fetch(`${API}/glossary/terms/${editingTerm.term_id}`, {
        method: 'PUT',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (res.ok) {
        toast.success('Begriff erfolgreich aktualisiert');
        setShowEditDialog(false);
        setEditingTerm(null);
        resetForm();
        fetchTerms();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Fehler beim Aktualisieren');
      }
    } catch (error) {
      toast.error('Fehler beim Aktualisieren des Begriffs');
    }
  };

  // Delete term (soft delete)
  const handleDelete = async (termId) => {
    if (!window.confirm('Möchten Sie diesen Begriff wirklich löschen? (Soft Delete)')) return;

    try {
      const res = await fetch(`${API}/glossary/terms/${termId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (res.ok) {
        toast.success('Begriff gelöscht (Soft Delete)');
        fetchTerms();
        fetchStats();
      } else {
        toast.error('Fehler beim Löschen');
      }
    } catch (error) {
      toast.error('Fehler beim Löschen des Begriffs');
    }
  };

  // Export glossary
  const handleExport = async () => {
    try {
      const res = await fetch(`${API}/glossary/export`, {
        headers: getAuthHeaders()
      });
      
      if (res.ok) {
        const data = await res.json();
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `gossa_export_${new Date().toISOString().split('T')[0]}.json`;
        a.click();
        toast.success('Glossar exportiert (GOSSA v2.0)');
      }
    } catch (error) {
      toast.error('Fehler beim Export');
    }
  };

  // Preview JSON before import
  const handlePreviewJson = () => {
    setImportPreviewError('');
    setImportPreview(null);
    
    if (!importJsonText.trim()) {
      setImportPreviewError('Bitte JSON-Inhalt einfügen');
      return;
    }
    
    try {
      const data = JSON.parse(importJsonText);
      const termsArray = data.terms || data;
      
      if (!Array.isArray(termsArray)) {
        setImportPreviewError('JSON muss ein Array oder ein Objekt mit "terms" Array sein');
        return;
      }
      
      if (termsArray.length === 0) {
        setImportPreviewError('Keine Begriffe im JSON gefunden');
        return;
      }
      
      // Validate and analyze terms
      const analysis = {
        total: termsArray.length,
        valid: 0,
        invalid: 0,
        withContexts: 0,
        categories: {},
        errors: [],
        terms: []
      };
      
      termsArray.forEach((term, index) => {
        const termInfo = {
          index: index + 1,
          term: term.term || '(kein Begriff)',
          master_definition: term.master_definition ? term.master_definition.substring(0, 80) + (term.master_definition.length > 80 ? '...' : '') : '(keine Definition)',
          category: term.category || 'General',
          contexts: term.context_definitions ? Object.keys(term.context_definitions) : [],
          tags: term.tags || [],
          isValid: true,
          errors: []
        };
        
        // Validation checks
        if (!term.term || typeof term.term !== 'string') {
          termInfo.isValid = false;
          termInfo.errors.push('Begriff fehlt');
        }
        if (!term.master_definition || typeof term.master_definition !== 'string') {
          termInfo.isValid = false;
          termInfo.errors.push('Master-Definition fehlt');
        }
        
        if (termInfo.isValid) {
          analysis.valid++;
        } else {
          analysis.invalid++;
          analysis.errors.push(`#${index + 1} "${term.term || 'Unbekannt'}": ${termInfo.errors.join(', ')}`);
        }
        
        if (termInfo.contexts.length > 0) {
          analysis.withContexts++;
        }
        
        // Count categories
        const cat = term.category || 'General';
        analysis.categories[cat] = (analysis.categories[cat] || 0) + 1;
        
        analysis.terms.push(termInfo);
      });
      
      setImportPreview(analysis);
    } catch (error) {
      setImportPreviewError('JSON-Parsing-Fehler: ' + (error.message || 'Unbekannter Fehler'));
    }
  };

  // Import from preview (confirmed)
  const handleImportFromPreview = async () => {
    if (!importPreview || importPreview.valid === 0) return;
    
    setImportLoading(true);
    try {
      await processImport(importJsonText);
      setImportPreview(null);
    } finally {
      setImportLoading(false);
    }
  };

  // Cancel preview
  const handleCancelPreview = () => {
    setImportPreview(null);
    setImportPreviewError('');
  };

  // Import glossary from file
  const handleImportFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setImportLoading(true);
    try {
      const text = await file.text();
      await processImport(text);
    } catch (error) {
      toast.error('Fehler beim Lesen der Datei');
    } finally {
      setImportLoading(false);
      event.target.value = '';
    }
  };

  // Import glossary from pasted JSON (direct, without preview)
  const handleImportPaste = async () => {
    if (!importJsonText.trim()) {
      toast.error('Bitte JSON-Inhalt einfügen');
      return;
    }
    
    setImportLoading(true);
    try {
      await processImport(importJsonText);
    } finally {
      setImportLoading(false);
    }
  };

  // Process import (shared logic)
  const processImport = async (jsonText) => {
    try {
      const data = JSON.parse(jsonText);
      
      const res = await fetch(`${API}/glossary/import`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          terms: data.terms || data,
          merge_strategy: importMergeStrategy
        })
      });

      if (res.ok) {
        const result = await res.json();
        toast.success(`Import: ${result.imported} erstellt, ${result.merged} zusammengeführt, ${result.skipped} übersprungen`);
        fetchTerms();
        fetchStats();
        setShowImportDialog(false);
        setImportJsonText('');
        setImportPreview(null);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Import fehlgeschlagen');
      }
    } catch (error) {
      toast.error('Ungültiges JSON-Format: ' + (error.message || 'Parsing-Fehler'));
    }
  };

  // Create indexes
  const handleCreateIndexes = async () => {
    try {
      const res = await fetch(`${API}/glossary/admin/create-indexes`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      
      if (res.ok) {
        const result = await res.json();
        toast.success(result.message);
      }
    } catch (error) {
      toast.error('Fehler beim Erstellen der Indizes');
    }
  };

  // Form dialog: extracted to glossary/GlossaryFormDialog.js. The same component is
  // mounted twice (add + edit) so React doesn't reuse state between modes.
  const formDialogProps = {
    formData,
    setFormData,
    newContextExam,
    setNewContextExam,
    newContextDef,
    setNewContextDef,
    newContextRef,
    setNewContextRef,
    addContextDefinition,
    removeContextDefinition,
    newReference,
    setNewReference,
    addReference,
    removeReference,
    newTag,
    setNewTag,
    addTag,
    removeTag,
    categories,
    examContexts: EXAM_CONTEXTS,
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <BookOpen className="w-6 h-6 text-indigo-600" />
            <div>
              <CardTitle>GOSSA - Glossar Management</CardTitle>
              <p className="text-sm text-slate-500 mt-1">Unified Knowledge Architecture v2.0</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {stats && (
              <div className="flex items-center gap-2 mr-2">
                <Badge variant="secondary">{stats.total} Begriffe</Badge>
                <Badge variant="outline">{stats.with_context_definitions || 0} mit Kontext</Badge>
              </div>
            )}
            <Button variant="outline" size="sm" onClick={handleExport} data-testid="export-btn">
              <Download className="w-4 h-4 mr-2" />
              Export
            </Button>
            <Button variant="outline" size="sm" onClick={() => setShowImportDialog(true)} data-testid="import-btn">
              <Upload className="w-4 h-4 mr-2" />
              Import
            </Button>
            <Button onClick={() => { resetForm(); setShowAddDialog(true); }} data-testid="add-term-btn">
              <Plus className="w-4 h-4 mr-2" />
              Neuer Begriff
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* Filters */}
        <GlossaryFilters
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          selectedCategory={selectedCategory}
          onCategoryChange={setSelectedCategory}
          categories={categories}
          loading={loading}
          onRefresh={fetchTerms}
        />

        {/* Terms Table */}
        <ScrollArea className="h-[500px] rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8"></TableHead>
                <TableHead className="w-48">Begriff</TableHead>
                <TableHead>Master-Definition</TableHead>
                <TableHead className="w-28">Kontexte</TableHead>
                <TableHead className="w-28">Kategorie</TableHead>
                <TableHead className="w-20 text-center">
                  <Languages className="w-4 h-4 mx-auto" title="Nicht übersetzen" />
                </TableHead>
                <TableHead className="w-24 text-right">Aktionen</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" />
                  </TableCell>
                </TableRow>
              ) : terms.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                    Keine Begriffe gefunden. Starten Sie mit dem Import oder erstellen Sie neue Begriffe.
                  </TableCell>
                </TableRow>
              ) : (
                terms.map(term => (
                  <React.Fragment key={term.term_id}>
                    <TableRow className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setExpandedRow(expandedRow === term.term_id ? null : term.term_id)}
                          className="h-6 w-6 p-0"
                        >
                          {expandedRow === term.term_id ? (
                            <ChevronUp className="w-4 h-4" />
                          ) : (
                            <ChevronDown className="w-4 h-4" />
                          )}
                        </Button>
                      </TableCell>
                      <TableCell className="font-medium">
                        {term.term}
                        {term.tags?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {term.tags.slice(0, 3).map((tag, idx) => (
                              <Badge key={idx} variant="outline" className="text-xs px-1 py-0">
                                {tag}
                              </Badge>
                            ))}
                            {term.tags.length > 3 && (
                              <Badge variant="outline" className="text-xs px-1 py-0">
                                +{term.tags.length - 3}
                              </Badge>
                            )}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="max-w-md">
                        <p className="text-sm text-slate-600 dark:text-slate-300 line-clamp-2">
                          {term.master_definition || term.definition}
                        </p>
                        {term.primary_source && (
                          <p className="text-xs text-slate-400 mt-1">
                            Quelle: {term.primary_source}
                          </p>
                        )}
                      </TableCell>
                      <TableCell>
                        {term.context_definitions && Object.keys(term.context_definitions).length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {Object.keys(term.context_definitions).map(ctx => (
                              <Badge key={ctx} variant="secondary" className="text-xs">
                                {ctx}
                              </Badge>
                            ))}
                          </div>
                        ) : (
                          <span className="text-slate-300">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{term.category}</Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        {term.do_not_translate ? (
                          <CheckCircle className="w-4 h-4 text-orange-500 mx-auto" title="Nicht übersetzen" />
                        ) : (
                          <span className="text-slate-300">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setPreviewTerm(term);
                            setShowPreviewDialog(true);
                          }}
                          title="Vorschau"
                        >
                          <Eye className="w-4 h-4 text-blue-500" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditDialog(term)}
                          data-testid={`edit-${term.term_id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(term.term_id)}
                          className="text-red-500 hover:text-red-700"
                          data-testid={`delete-${term.term_id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                    {/* Expanded row with context definitions */}
                    {expandedRow === term.term_id && (
                      <TableRow className="bg-slate-50 dark:bg-slate-800/30">
                        <TableCell colSpan={7} className="p-4">
                          <div className="grid grid-cols-2 gap-4">
                            {/* Context Definitions */}
                            <div>
                              <h4 className="font-semibold text-sm mb-2 flex items-center gap-2">
                                <Globe className="w-4 h-4" />
                                Kontext-Definitionen
                              </h4>
                              {term.context_definitions && Object.keys(term.context_definitions).length > 0 ? (
                                <div className="space-y-2">
                                  {Object.entries(term.context_definitions).map(([ctx, data]) => (
                                    <div key={ctx} className="p-2 bg-white dark:bg-slate-800 rounded border text-sm">
                                      <Badge className="mb-1">{ctx}</Badge>
                                      <p className="text-slate-600 dark:text-slate-300">{data.definition}</p>
                                      {data.reference && (
                                        <p className="text-xs text-slate-400 mt-1">Ref: {data.reference}</p>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <p className="text-sm text-slate-400">Keine Kontext-Definitionen</p>
                              )}
                            </div>
                            {/* References */}
                            <div>
                              <h4 className="font-semibold text-sm mb-2 flex items-center gap-2">
                                <FileText className="w-4 h-4" />
                                Referenzen
                              </h4>
                              {term.references?.length > 0 ? (
                                <ul className="list-disc list-inside text-sm text-slate-600 dark:text-slate-300">
                                  {term.references.map((ref, idx) => (
                                    <li key={idx}>{ref}</li>
                                  ))}
                                </ul>
                              ) : (
                                <p className="text-sm text-slate-400">Keine zusätzlichen Referenzen</p>
                              )}
                              {term.version && (
                                <p className="text-xs text-slate-400 mt-2">Version: {term.version}</p>
                              )}
                            </div>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))
              )}
            </TableBody>
          </Table>
        </ScrollArea>

        {/* Stats Summary */}
        <GlossaryStatsSummary stats={stats} onCategoryClick={setSelectedCategory} />
      </CardContent>

      {/* Add / Edit dialogs (same component, separate state) */}
      <GlossaryFormDialog
        mode="add"
        open={showAddDialog}
        onOpenChange={setShowAddDialog}
        onSubmit={handleCreate}
        {...formDialogProps}
      />
      <GlossaryFormDialog
        mode="edit"
        open={showEditDialog}
        onOpenChange={setShowEditDialog}
        onSubmit={handleUpdate}
        {...formDialogProps}
      />

      {/* Preview Dialog */}
      <GlossaryPreviewDialog
        open={showPreviewDialog}
        onOpenChange={setShowPreviewDialog}
        term={previewTerm}
        onEdit={openEditDialog}
      />

      {/* Import Dialog */}
      <Dialog open={showImportDialog} onOpenChange={(open) => {
        setShowImportDialog(open);
        if (!open) {
          setImportJsonText('');
          setImportMethod('file');
          setImportPreview(null);
          setImportPreviewError('');
        }
      }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5" />
              GOSSA Import v2.0
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-amber-800 dark:text-amber-200">Merge-Strategie</h4>
                  <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                    Wählen Sie, wie mit existierenden Begriffen umgegangen werden soll.
                  </p>
                </div>
              </div>
            </div>
            
            <Select value={importMergeStrategy} onValueChange={setImportMergeStrategy}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="skip">
                  <div>
                    <span className="font-medium">Überspringen</span>
                    <span className="text-slate-500 ml-2">- Existierende Begriffe ignorieren</span>
                  </div>
                </SelectItem>
                <SelectItem value="merge">
                  <div>
                    <span className="font-medium">Zusammenführen</span>
                    <span className="text-slate-500 ml-2">- Kontext-Definitionen und Referenzen ergänzen</span>
                  </div>
                </SelectItem>
                <SelectItem value="overwrite">
                  <div>
                    <span className="font-medium">Überschreiben</span>
                    <span className="text-slate-500 ml-2">- Existierende Begriffe ersetzen</span>
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>

            {/* Import Method Tabs */}
            <Tabs value={importMethod} onValueChange={setImportMethod} className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="file" className="flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Datei hochladen
                </TabsTrigger>
                <TabsTrigger value="paste" className="flex items-center gap-2">
                  <Globe className="w-4 h-4" />
                  JSON einfügen
                </TabsTrigger>
              </TabsList>

              {/* File Upload Tab */}
              <TabsContent value="file" className="space-y-4 mt-4">
                <div className="p-4 bg-slate-100 dark:bg-slate-800 rounded">
                  <p className="text-sm text-slate-600 dark:text-slate-300 mb-2">
                    JSON-Datei auswählen (GOSSA v2.0 Format):
                  </p>
                  <Input
                    type="file"
                    accept=".json"
                    onChange={handleImportFile}
                    disabled={importLoading}
                    data-testid="import-file-input"
                    className="cursor-pointer"
                  />
                </div>
              </TabsContent>

              {/* JSON Paste Tab */}
              <TabsContent value="paste" className="space-y-4 mt-4">
                {!importPreview ? (
                  // Input Mode
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="json-paste" className="text-sm font-medium">
                        JSON-Inhalt direkt einfügen:
                      </Label>
                      <Textarea
                        id="json-paste"
                        value={importJsonText}
                        onChange={(e) => {
                          setImportJsonText(e.target.value);
                          setImportPreviewError('');
                        }}
                        placeholder={`{
  "terms": [
    {
      "term": "Sprint",
      "master_definition": "Normative Definition...",
      "primary_source": "Scrum Guide 2020",
      ...
    }
  ]
}`}
                        rows={10}
                        className="font-mono text-sm"
                        disabled={importLoading}
                        data-testid="import-json-textarea"
                      />
                      <p className="text-xs text-slate-500">
                        Fügen Sie hier den kompletten JSON-Inhalt ein. Format: Array von Begriffen oder Objekt mit "terms" Array.
                      </p>
                    </div>
                    
                    {/* Preview Error */}
                    {importPreviewError && (
                      <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                        <div className="flex items-center gap-2 text-red-700 dark:text-red-300">
                          <AlertCircle className="w-4 h-4" />
                          <span className="text-sm">{importPreviewError}</span>
                        </div>
                      </div>
                    )}
                    
                    {/* Action Buttons */}
                    <div className="flex gap-2">
                      <Button 
                        onClick={handlePreviewJson}
                        disabled={importLoading || !importJsonText.trim()}
                        variant="outline"
                        className="flex-1"
                        data-testid="preview-json-btn"
                      >
                        <Eye className="w-4 h-4 mr-2" />
                        Vorschau
                      </Button>
                      <Button 
                        onClick={handleImportPaste}
                        disabled={importLoading || !importJsonText.trim()}
                        className="flex-1"
                        data-testid="import-paste-btn"
                      >
                        {importLoading ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Importiere...
                          </>
                        ) : (
                          <>
                            <Upload className="w-4 h-4 mr-2" />
                            Direkt importieren
                          </>
                        )}
                      </Button>
                    </div>
                  </>
                ) : (
                  // Preview Mode
                  <div className="space-y-4">
                    {/* Summary Stats */}
                    <div className="p-4 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg border border-indigo-200 dark:border-indigo-800">
                      <h4 className="font-semibold text-indigo-800 dark:text-indigo-200 mb-3 flex items-center gap-2">
                        <Eye className="w-4 h-4" />
                        Import-Vorschau
                      </h4>
                      <div className="grid grid-cols-4 gap-3 text-sm">
                        <div className="text-center p-2 bg-white dark:bg-slate-800 rounded">
                          <div className="text-2xl font-bold text-indigo-600">{importPreview.total}</div>
                          <div className="text-slate-500">Gesamt</div>
                        </div>
                        <div className="text-center p-2 bg-white dark:bg-slate-800 rounded">
                          <div className="text-2xl font-bold text-green-600">{importPreview.valid}</div>
                          <div className="text-slate-500">Gültig</div>
                        </div>
                        <div className="text-center p-2 bg-white dark:bg-slate-800 rounded">
                          <div className="text-2xl font-bold text-red-600">{importPreview.invalid}</div>
                          <div className="text-slate-500">Ungültig</div>
                        </div>
                        <div className="text-center p-2 bg-white dark:bg-slate-800 rounded">
                          <div className="text-2xl font-bold text-blue-600">{importPreview.withContexts}</div>
                          <div className="text-slate-500">Mit Kontext</div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Categories Overview */}
                    {Object.keys(importPreview.categories).length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        <span className="text-sm text-slate-500">Kategorien:</span>
                        {Object.entries(importPreview.categories).map(([cat, count]) => (
                          <Badge key={cat} variant="secondary">
                            {cat}: {count}
                          </Badge>
                        ))}
                      </div>
                    )}
                    
                    {/* Validation Errors */}
                    {importPreview.errors.length > 0 && (
                      <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                        <h5 className="font-medium text-red-700 dark:text-red-300 mb-2 flex items-center gap-2">
                          <AlertCircle className="w-4 h-4" />
                          Validierungsfehler ({importPreview.errors.length})
                        </h5>
                        <ul className="text-sm text-red-600 dark:text-red-400 space-y-1 max-h-24 overflow-y-auto">
                          {importPreview.errors.map((err, idx) => (
                            <li key={idx}>• {err}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    {/* Terms Preview List */}
                    <div className="border rounded-lg overflow-hidden">
                      <div className="bg-slate-100 dark:bg-slate-800 px-3 py-2 border-b">
                        <span className="text-sm font-medium">Begriffe ({importPreview.terms.length})</span>
                      </div>
                      <ScrollArea className="h-48">
                        <div className="divide-y">
                          {importPreview.terms.slice(0, 50).map((term, idx) => (
                            <div 
                              key={idx} 
                              className={`px-3 py-2 text-sm ${!term.isValid ? 'bg-red-50 dark:bg-red-900/10' : ''}`}
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <span className="text-slate-400">#{term.index}</span>
                                  <span className="font-medium">{term.term}</span>
                                  {term.isValid ? (
                                    <CheckCircle className="w-3 h-3 text-green-500" />
                                  ) : (
                                    <AlertCircle className="w-3 h-3 text-red-500" />
                                  )}
                                </div>
                                <div className="flex items-center gap-1">
                                  <Badge variant="outline" className="text-xs">{term.category}</Badge>
                                  {term.contexts.length > 0 && (
                                    <Badge variant="secondary" className="text-xs">
                                      {term.contexts.length} Kontext{term.contexts.length > 1 ? 'e' : ''}
                                    </Badge>
                                  )}
                                </div>
                              </div>
                              <p className="text-slate-500 text-xs mt-1 line-clamp-1">
                                {term.master_definition}
                              </p>
                              {!term.isValid && (
                                <p className="text-red-500 text-xs mt-1">
                                  Fehler: {term.errors.join(', ')}
                                </p>
                              )}
                            </div>
                          ))}
                          {importPreview.terms.length > 50 && (
                            <div className="px-3 py-2 text-sm text-slate-500 text-center">
                              ... und {importPreview.terms.length - 50} weitere Begriffe
                            </div>
                          )}
                        </div>
                      </ScrollArea>
                    </div>
                    
                    {/* Action Buttons */}
                    <div className="flex gap-2">
                      <Button 
                        onClick={handleCancelPreview}
                        variant="outline"
                        className="flex-1"
                      >
                        Zurück bearbeiten
                      </Button>
                      <Button 
                        onClick={handleImportFromPreview}
                        disabled={importLoading || importPreview.valid === 0}
                        className="flex-1 bg-green-600 hover:bg-green-700"
                        data-testid="confirm-import-btn"
                      >
                        {importLoading ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Importiere...
                          </>
                        ) : (
                          <>
                            <CheckCircle className="w-4 h-4 mr-2" />
                            {importPreview.valid} Begriffe importieren
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                )}
              </TabsContent>
            </Tabs>
            
            {/* Format Reference (collapsible) */}
            <details className="p-4 bg-slate-100 dark:bg-slate-800 rounded">
              <summary className="text-sm font-medium text-slate-600 dark:text-slate-300 cursor-pointer select-none">
                📋 Erwartetes JSON-Format (GOSSA v2.0)
              </summary>
              <pre className="bg-white dark:bg-slate-900 p-3 rounded text-xs overflow-auto max-h-48 mt-2">
{`{
  "terms": [
    {
      "term": "Sprint",
      "master_definition": "Normative Definition...",
      "primary_source": "Scrum Guide 2020",
      "context_definitions": {
        "CAPM": {
          "definition": "CAPM-spezifische Definition...",
          "reference": "CAPM Glossary 2024"
        }
      },
      "references": ["Scrum Guide 2020", "CAPM Glossary"],
      "category": "Agile",
      "tags": ["Scrum", "Timebox"],
      "do_not_translate": true,
      "version": "1.0"
    }
  ]
}`}
              </pre>
            </details>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowImportDialog(false)}>
              Abbrechen
            </Button>
            <Button variant="outline" onClick={handleCreateIndexes}>
              DB Indizes erstellen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default AdminGlossaryTab;
